/**
 * Widget renderers for the Dashboard view. All of them are pure functions of
 * the board's in-memory rows (via `lib/dashboard` aggregations) — no fetching
 * except the shared workspace-user lookup that maps people-column ids to
 * names (deduped by TanStack Query across all widgets).
 *
 * Charts are recharts, colored with the exact status colors users configured
 * on the board (falling back to the group palette), so the dashboard reads as
 * the same product surface as the table.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { DashboardWidget } from '@/entities/tracker';
import { workspaceUsersQuery } from '@/entities/project-member';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import {
  countByColumn,
  NO_VALUE_KEY,
  numberStats,
  timeSeries,
  type CategoryDatum,
  type DashboardItem,
} from '../../lib/dashboard';
import type { CustomColumn } from '../../lib/useTrackersBoard';
import { GROUP_COLORS } from '../TrackersProjectGroup';
import { useTranslation } from 'react-i18next';

interface WidgetDataProps {
  widget: DashboardWidget;
  items: DashboardItem[];
  /** Resolved from `widget.config.columnId`; null when absent/deleted. */
  column: CustomColumn | null;
}

const colorFor = (datum: CategoryDatum, index: number): string =>
  datum.color ?? GROUP_COLORS[index % GROUP_COLORS.length];

const EmptyData = () => {
  const { t } = useTranslation('trackers');
  return (
    <div className="flex h-full min-h-24 items-center justify-center text-xs text-muted-foreground">
      {t('trackersBoard.dashboardWidgets.noDataYet')}
    </div>
  );
};

/**
 * Category buckets for the widget's column, with people ids resolved to
 * workspace-user names. All widgets share ONE users query (same key).
 */
function useCategoryData(items: DashboardItem[], column: CustomColumn | null): CategoryDatum[] {
  const workspaceId = useCurrentWorkspaceId();
  const isPeople = column?.type === 'people';
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && isPeople,
  });

  return useMemo(() => {
    if (!column) return [];
    const data = countByColumn(items, column);
    if (!isPeople) return data;
    const users = usersQuery.data?.data ?? [];
    const nameById = new Map(users.map((u) => [u.id, u.name]));
    return data.map((d) =>
      d.key === NO_VALUE_KEY ? d : { ...d, label: nameById.get(d.key) ?? d.label },
    );
  }, [items, column, isPeople, usersQuery.data]);
}

// ─── KPI ─────────────────────────────────────────────────────────────────────

export function KpiWidget({ widget, items, column }: WidgetDataProps) {
  const { t } = useTranslation('trackers');

  const agg = widget.config?.agg ?? 'count';
  let value: number;
  let hint: string | null = null;

  if (agg === 'count' || !column) {
    value = items.length;
  } else {
    const stats = numberStats(items, column.id);
    value = agg === 'sum' ? stats.sum : stats.avg;
    hint = t('trackersBoard.dashboardWidgets.acrossItems', { count: stats.filled });
  }

  const formatted =
    Number.isInteger(value) && agg !== 'avg'
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 1 });

  return (
    <div className="flex h-full min-h-20 flex-col items-start justify-center gap-1">
      <div className="text-4xl font-semibold tracking-tight text-foreground">{formatted}</div>
      {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

// ─── Donut ───────────────────────────────────────────────────────────────────

export function DonutWidget({ items, column }: WidgetDataProps) {
  const { t } = useTranslation('trackers');

  const data = useCategoryData(items, column);
  const total = data.reduce((sum, d) => sum + d.count, 0);
  if (total === 0) return <EmptyData />;

  return (
    <div className="flex h-full items-center gap-4">
      <div className="relative h-full flex-1">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              // recharts v3 reads per-slice `fill` from the data entries
              // (the former <Cell> children API is deprecated).
              data={data.map((d, i) => ({ ...d, fill: colorFor(d, i) }))}
              dataKey="count"
              nameKey="label"
              innerRadius="62%"
              outerRadius="90%"
              paddingAngle={2}
              strokeWidth={0}
              isAnimationActive={false}
            />
            {/* isAnimationActive={false}: recharts animates the tooltip's
                position by default (~400ms), which trails/ghosts behind a
                fast-moving cursor. Snap it to the pointer instead. */}
            <Tooltip isAnimationActive={false} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-foreground">{total}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('trackersBoard.dashboardWidgets.items')}
          </span>
        </div>
      </div>
      <ul className="max-h-full w-36 shrink-0 space-y-1.5 overflow-y-auto text-xs">
        {data.map((d, i) => (
          <li key={d.key} className="flex items-center gap-2">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: colorFor(d, i) }}
            />
            <span className="min-w-0 flex-1 truncate text-foreground" title={d.label}>
              {d.label}
            </span>
            <span className="text-muted-foreground">{d.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Bar ─────────────────────────────────────────────────────────────────────

export function BarWidget({ items, column }: WidgetDataProps) {
  const data = useCategoryData(items, column);
  if (data.length === 0) return <EmptyData />;

  return (
    <div className="h-full text-muted-foreground">
      <ResponsiveContainer>
        <BarChart
          // Per-bar `fill` rides in the data entries (recharts v3 API).
          data={data.map((d, i) => ({ ...d, fill: colorFor(d, i) }))}
          margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
        >
          <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.12} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            interval={0}
            tickFormatter={(label: string) => (label.length > 10 ? `${label.slice(0, 9)}…` : label)}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
          />
          <Tooltip cursor={{ fillOpacity: 0.06 }} isAnimationActive={false} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Line (over time) ────────────────────────────────────────────────────────

export function LineWidget({ widget, items, column }: WidgetDataProps) {
  const bucket = widget.config?.dateBucket ?? 'week';
  const data = useMemo(() => timeSeries(items, column, bucket), [items, column, bucket]);
  if (data.length === 0) return <EmptyData />;
  const stroke = GROUP_COLORS[0];

  return (
    <div className="h-full text-muted-foreground">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id={`dash-line-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.25} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.12} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
          />
          <Tooltip cursor={{ strokeOpacity: 0.2 }} isAnimationActive={false} />
          <Area
            type="monotone"
            dataKey="count"
            stroke={stroke}
            strokeWidth={2}
            fill={`url(#dash-line-${widget.id})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Battery (status progress) ───────────────────────────────────────────────

export function BatteryWidget({ items, column }: WidgetDataProps) {
  const data = useCategoryData(items, column);
  const total = data.reduce((sum, d) => sum + d.count, 0);
  if (total === 0) return <EmptyData />;

  return (
    <div className="flex h-full min-h-24 flex-col justify-center gap-3">
      <div className="flex h-7 w-full overflow-hidden rounded-md">
        {data.map((d, i) => (
          <div
            key={d.key}
            className="h-full"
            style={{ width: `${(d.count / total) * 100}%`, backgroundColor: colorFor(d, i) }}
            title={`${d.label}: ${d.count} (${Math.round((d.count / total) * 100)}%)`}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {data.map((d, i) => (
          <li key={d.key} className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full" style={{ backgroundColor: colorFor(d, i) }} />
            <span className="text-foreground">{d.label}</span>
            <span className="text-muted-foreground">{Math.round((d.count / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Text block ──────────────────────────────────────────────────────────────

export function TextWidget({ widget }: WidgetDataProps) {
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
      {widget.config?.text ?? ''}
    </p>
  );
}

/** Dispatch a widget config to its renderer. */
export function DashboardWidgetBody(props: WidgetDataProps) {
  switch (props.widget.type) {
    case 'kpi':
      return <KpiWidget {...props} />;
    case 'donut':
      return <DonutWidget {...props} />;
    case 'bar':
      return <BarWidget {...props} />;
    case 'line':
      return <LineWidget {...props} />;
    case 'battery':
      return <BatteryWidget {...props} />;
    case 'text':
      return <TextWidget {...props} />;
    default:
      return <EmptyData />;
  }
}
